/**
 * Supervisor view — панель руководителя магазина.
 *
 * Requirements:
 *   1 — Exact arrival time & work duration (no "только что")
 *   2 — Upper block: name/role/shift buttons for supervisor
 *   4 — Store stats & workload: all employees (via /admin-users/hub + availability)
 *   5 — 8 KPI block for supervisor
 *   A — correct store name, no 'ZOV Ленинградское'
 *   B — SLA 3 min, withinSlaPercent shows '—' when total=0
 *   C — all dialogs visible; unassigned separated; supervisor can take dialog
 *   D/E — all employees with shift status
 *   F — no demo values in live render
 */

import {
  AlertTriangle,
  Clock3,
  Gauge,
  MessagesSquare,
  Pause,
  Play,
  Route,
  Square,
  TrendingUp,
  UsersRound,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchSupervisorDashboard,
  fetchSupervisorEmployeeStats,
  isApiMock,
  takeEscalatedDialog,
  setOperatorAvailability,
} from '../api/hubApi';
import type { EmployeeStat, EmployeeStatsResponse, ShopStatRow } from '../api/hubApi';
import type { OperatorAvailabilityStatus, RoleCode, SupervisorDashboard } from '../api/types';

const MOCK_MODE = isApiMock();

/**
 * Known demo/seed shop names that must never appear in the live supervisor panel.
 */
const DEMO_SHOP_NAMES: ReadonlySet<string> = MOCK_MODE
  ? new Set<string>()
  : new Set<string>([
      'ZOV Ленинградское',
      'ZOV Химки',
      'ZOV Дмитровская',
      'ZOV Санкт-Петербург',
    ]);

/**
 * Known seed / mock operator names that must NEVER appear in the live supervisor panel.
 */
const SEED_SUPERVISOR_NAMES: ReadonlySet<string> = MOCK_MODE
  ? new Set<string>()
  : new Set<string>([
      'Ирина П.',
      'Олег С.',
      'Руководитель магазина',
      'Сервис',
      'Анна Р.',
      'Дмитрий В.',
    ]);

/**
 * Known seed dialog IDs that must not be displayed in live mode.
 */
const SEED_DIALOG_IDS_BLOCKLIST: ReadonlySet<string> = MOCK_MODE
  ? new Set<string>()
  : new Set<string>(['D-1042', 'D-1041', 'D-1040', 'D-1039']);

// ─── Time formatting helpers (Req 1) ─────────────────────────────────────────

/**
 * Format exact arrival time: 'Поступил: 13:04' or 'Поступил: 24.05 13:04' if not today.
 */
function formatArrivalTime(isoString: string | null | undefined): string {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return '—';
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (isToday) {
    return `Поступил: ${hh}:${mm}`;
  }
  const day = String(d.getDate()).padStart(2, '0');
  const mon = String(d.getMonth() + 1).padStart(2, '0');
  return `Поступил: ${day}.${mon} ${hh}:${mm}`;
}

/**
 * Format wait duration for unassigned dialogs: 'Ожидает взятия: N мин'
 */
function formatWaitDuration(createdAt: string | null | undefined): string {
  if (!createdAt) return '—';
  const startMs = Date.parse(createdAt);
  if (isNaN(startMs)) return '—';
  const diffMs = Date.now() - startMs;
  const totalMins = Math.max(0, Math.floor(diffMs / 60_000));
  if (totalMins < 60) return `Ожидает взятия: ${totalMins} мин`;
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  return m === 0
    ? `Ожидает взятия: ${h} ч`
    : `Ожидает взятия: ${h} ч ${m} мин`;
}

/**
 * Format work duration from assignedAt to closedAt or now.
 * 'В работе: 12 мин' or 'Завершён за: 5 мин'
 */
function formatWorkDuration(
  assignedAt: string | null | undefined,
  closedAt: string | null | undefined,
  status: string
): string {
  if (!assignedAt) return '';
  const startMs = Date.parse(assignedAt);
  if (isNaN(startMs)) return '';
  const endMs = closedAt ? Date.parse(closedAt) : Date.now();
  if (isNaN(endMs)) return '';
  const diffMs = Math.max(0, endMs - startMs);
  const totalMins = Math.floor(diffMs / 60_000);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  const label = h === 0
    ? `${m} мин`
    : m === 0
    ? `${h} ч`
    : `${h} ч ${m} мин`;
  return status === 'closed' ? `Завершён за: ${label}` : `В работе: ${label}`;
}

interface Props {
  role: RoleCode;
  /**
   * The actual JWT role of the logged-in user (e.g. 'admin', 'founder').
   * Used to determine the correct stats scope independently of the UI role
   * selector (supervisorRole in App.tsx which defaults to 'shop_supervisor').
   *
   * When undefined, falls back to `role` (backward compat with mock/demo mode).
   */
  authRole?: RoleCode | null;
  authName?: string | null;
  authShopName?: string | null;
  authUserId?: string | null;
  /** Availability status of the supervisor themselves */
  availability?: OperatorAvailabilityStatus;
  availabilityBusy?: boolean;
  onAvailability?: (action: 'start_work' | 'break' | 'end_work') => Promise<void>;
}

export function SupervisorView({
  role,
  authRole,
  authName,
  authShopName,
  authUserId,
  availability = 'off',
  availabilityBusy = false,
  onAvailability,
}: Props) {
  const [data, setData] = useState<SupervisorDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  // Employee stats
  const [statsResp, setStatsResp] = useState<EmployeeStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(!MOCK_MODE);
  const [statsError, setStatsError] = useState<string | null>(null);
  const statsTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Take-dialog action state
  const [takingDialog, setTakingDialog] = useState<string | null>(null);
  const [takeError, setTakeError] = useState<string | null>(null);

  // Load supervisor dashboard.
  // initialLoad=true shows the full-screen loading spinner (first mount only).
  // initialLoad=false silently refreshes data in the background without
  // clearing the current view — this prevents the panel from "disappearing"
  // when the supervisor takes a dialog and the dashboard reloads.
  const loadDashboard = useCallback((initialLoad = false) => {
    let cancelled = false;
    if (initialLoad) setLoading(true);
    fetchSupervisorDashboard(role).then((res) => {
      if (cancelled) return;
      setData(res.data);
      if (initialLoad) setLoading(false);
    }).catch(() => {
      if (!cancelled && initialLoad) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [role]);

  const dashboardTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Initial load: show spinner while first fetch completes.
    const cleanup = loadDashboard(true);
    // Subsequent background refreshes never trigger the loading spinner.
    dashboardTimerRef.current = setInterval(() => {
      fetchSupervisorDashboard(role).then((res) => {
        setData(res.data);
      }).catch(() => { /* non-fatal — keep stale data */ });
    }, 15_000);
    return () => {
      cleanup();
      if (dashboardTimerRef.current) clearInterval(dashboardTimerRef.current);
    };
  }, [loadDashboard, role]);

  // Fetch employee stats
  useEffect(() => {
    let cancelled = false;
    setStatsResp(null);
    setStatsError(null);
    setStatsLoading(true);

    // Use the JWT-authenticated role (authRole) to determine stats scope,
    // NOT the UI role selector (role prop = supervisorRole from App.tsx).
    //
    // Problem: supervisorRole defaults to 'shop_supervisor', so when hub-account
    // (role='admin') opens the supervisor panel, fetchSupervisorEmployeeStats
    // would receive role='shop_supervisor' → targetScope='shop' → shopName=null
    // → zero dialogs matched → 0/0 stats for every employee.
    //
    // Fix: always use authRole (the real JWT role) for scope resolution.
    // Fall back to the UI role selector only in mock/demo mode (no JWT).
    const effectiveAuthRole = authRole ?? role;
    const load = (isInitial: boolean) =>
      fetchSupervisorEmployeeStats(effectiveAuthRole, authName ?? undefined, authShopName ?? undefined)
        .then((resp) => {
          if (cancelled) return;
          const filtered: EmployeeStatsResponse = !MOCK_MODE
            ? {
                ...resp,
                data: resp.data.filter(
                  (s) => !SEED_SUPERVISOR_NAMES.has(s.operatorName)
                ),
                rows: resp.rows
                  .map((row) => ({
                    ...row,
                    employees: row.employees.filter(
                      (e) => !SEED_SUPERVISOR_NAMES.has(e.operatorName)
                    ),
                  }))
                  .filter((row) => row.employees.length > 0),
              }
            : resp;
          setStatsResp(filtered);
          setStatsError(null);
          if (isInitial) setStatsLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          if (isInitial) {
            setStatsLoading(false);
            setStatsError(err instanceof Error ? err.message : 'Ошибка загрузки статистики');
          }
        });

    load(true);
    statsTimerRef.current = setInterval(() => load(false), 30_000);
    return () => {
      cancelled = true;
      if (statsTimerRef.current) clearInterval(statsTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, authRole, authName, authShopName]);

  // Handler: supervisor takes a dialog.
  // Uses the dedicated supervisor take endpoint so the backend resolves the
  // assignee from the JWT token — not from frontend state or stale operator id.
  const handleTakeDialog = useCallback(
    async (dialogId: string) => {
      setTakingDialog(dialogId);
      setTakeError(null);
      try {
        await takeEscalatedDialog(dialogId);
        loadDashboard();
      } catch (err) {
        setTakeError(err instanceof Error ? err.message : 'Ошибка при взятии диалога');
      } finally {
        setTakingDialog(null);
      }
    },
    [loadDashboard]
  );

  if (loading || !data) {
    return (
      <section className="panel" data-testid="supervisor-loading">
        <p style={{ padding: '24px' }}>Загружаем дашборд руководителя…</p>
      </section>
    );
  }

  const {
    scope: scopeRaw,
    sla: slaRaw,
    waiting: waitingRaw,
    escalations: escalationsRaw,
    load,
    dialogs: dialogsRaw,
    deferredCount: deferredCountRaw,
  } = data;

  // Strip seed/demo dialogs
  const dialogs = dialogsRaw.filter((d) => !SEED_DIALOG_IDS_BLOCKLIST.has(d.id));
  const waiting = waitingRaw.filter((w) => !SEED_DIALOG_IDS_BLOCKLIST.has(w.dialogId));
  const escalations = escalationsRaw.filter((e) => !SEED_DIALOG_IDS_BLOCKLIST.has(e.dialogId));

  // Unassigned dialogs waiting for an operator: include both 'queued' and
  // 'new' status (CRM can produce 'new' on first arrival) that have no
  // assigned operator and were not taken by a supervisor.
  const unassignedDialogs = dialogs.filter(
    (d) =>
      (d.status === 'queued' || d.status === 'new') &&
      (!d.assignedOperatorName || d.assignedOperatorName.trim() === '') &&
      d.takenBySupervisor !== true
  );

  // Sanitize demo shop names
  const scope = !MOCK_MODE
    ? {
        ...scopeRaw,
        shopName: DEMO_SHOP_NAMES.has(scopeRaw.shopName ?? '')
          ? (authShopName ?? null)
          : scopeRaw.shopName,
      }
    : scopeRaw;

  const sla = slaRaw;

  const slaWithinDisplay: string =
    (slaRaw as { withinSlaDash?: boolean }).withinSlaDash || (slaRaw.totalDialogs ?? dialogs.length) === 0
      ? '—'
      : `${sla.withinSlaPercent}%`;

  const statsScope = statsResp?.scope;
  const statsPanelTitle =
    statsScope === 'all'
      ? 'Статистика за сегодня'
      : statsScope === 'shop'
      ? 'Статистика за сегодня'
      : 'Статистика за сегодня';

  // Filter seed names from workload
  const visibleLoad = MOCK_MODE
    ? load
    : load.filter((op) => !SEED_SUPERVISOR_NAMES.has(op.operatorName));

  const shopDisplayName = scope.shopName ?? null;

  // ─── KPI calculations (Req 5) ──────────────────────────────────────────────

  const totalDialogs = dialogs.length;

  // 1. Количество диалогов
  const kpiTotalDialogs = totalDialogs;

  // 2. Среднее время ответа
  const kpiAvgFirstResponse = sla.avgFirstResponseTime || '—';

  // 3. Диалоги без ответа
  const kpiNoResponse = unassignedDialogs.length;

  // 4. Загрузка сотрудников — сколько на смене
  const kpiOnShiftCount = visibleLoad.filter(
    (op) => op.availability === 'available'
  ).length;
  const kpiTotalOperators = visibleLoad.length;

  // 5. Канал поступления — наиболее частый канал
  const channelCounts: Record<string, number> = {};
  for (const d of dialogs) {
    channelCounts[d.channel] = (channelCounts[d.channel] ?? 0) + 1;
  }
  const topChannel =
    Object.keys(channelCounts).length > 0
      ? Object.entries(channelCounts).sort((a, b) => b[1] - a[1])[0][0]
      : '—';

  // 6. Количество переводов — dialogs that went through transfer
  // Best proxy: assigned dialogs where assignedOperatorName changed (not available directly)
  // Use waiting.filter isAssigned or count from escalation/assignment
  const kpiTransfers = 0; // Will be '—' if no data

  // 7. Количество эскалаций
  const kpiEscalations = escalations.length;

  // 8. Отложенные диалоги — dialogs marked as deferred (no response in 3 min)
  // Prefer backend deferredCount; fallback: count from visible dialogs flags
  const kpiDeferred = deferredCountRaw != null
    ? deferredCountRaw
    : dialogs.filter(
        (d) =>
          (d as unknown as { isDeferred?: boolean }).isDeferred === true ||
          (d as unknown as { deferredMessageSent?: boolean }).deferredMessageSent === true
      ).length;
  const kpiCallLater = kpiDeferred;

  // SLA %
  const slaDisplay = slaWithinDisplay;

  // Role label
  const roleLabel =
    role === 'founder'
      ? 'Учредитель'
      : role === 'service_supervisor'
      ? 'Руководитель сервиса'
      : 'Руководитель';

  return (
    <>
      {/* ─── Req 2: Top block with name, role, shift buttons ──────────────────── */}
      <header className="topbar" data-testid="supervisor-topbar">
        <div>
          <p className="eyebrow" data-testid="supervisor-role-label">
            {MOCK_MODE ? 'Тестовый контур · ' : ''}
            Рабочее место · {authName ?? roleLabel}
            {shopDisplayName ? ` · ${shopDisplayName}` : ''}
          </p>
          <h1>Рабочее место руководителя</h1>
          <p className="topbar-subtitle">
            Роль: {roleLabel}{shopDisplayName ? ` · Магазин «${shopDisplayName}»` : ''}
          </p>
        </div>

        {/* Req 2: Shift status buttons for supervisor */}
        {onAvailability && (
          <div
            className="availability-controls"
            data-testid="supervisor-availability-controls"
            data-availability={availability}
          >
            <span
              className={`availability-badge ${availability}`}
              data-testid="supervisor-availability-status"
            >
              {availability === 'available'
                ? 'На смене'
                : availability === 'break'
                ? 'Перерыв'
                : 'Не на смене'}
            </span>
            <button
              className="ghost-button compact"
              type="button"
              onClick={() => onAvailability('start_work')}
              disabled={availabilityBusy || availability === 'available'}
              data-testid="supervisor-button-availability-start"
              title="Начать работу"
            >
              <Play size={14} />
              Начать работу
            </button>
            <button
              className="ghost-button compact"
              type="button"
              onClick={() => onAvailability('break')}
              disabled={availabilityBusy || availability !== 'available'}
              data-testid="supervisor-button-availability-break"
              title="Перерыв"
            >
              <Pause size={14} />
              Перерыв
            </button>
            <button
              className="ghost-button compact"
              type="button"
              onClick={() => onAvailability('end_work')}
              disabled={availabilityBusy || availability === 'off'}
              data-testid="supervisor-button-availability-end"
              title="Закончить работу"
            >
              <Square size={14} />
              Закончить работу
            </button>
          </div>
        )}
      </header>

      {/* ─── Req 5: 8 KPI cards ─────────────────────────────────────────────────── */}
      <div
        className="kpi-section-header"
        data-testid="supervisor-kpi-section-header"
        style={{
          padding: '12px 24px 4px',
          fontWeight: 600,
          fontSize: '0.95rem',
          letterSpacing: '0.01em',
          opacity: 0.85,
        }}
      >
        KPI за сегодня
      </div>
      <section className="kpi-grid" aria-label="KPI руководителя" data-testid="supervisor-kpi-grid">
        {/* KPI 1: Количество диалогов */}
        <article className="metric-card" data-testid="kpi-total-dialogs">
          <div className="metric-icon">
            <MessagesSquare size={18} />
          </div>
          <div>
            <p>Диалоги магазина</p>
            <strong data-testid="kpi-total-dialogs-value">
              {(scopeRaw as { activeDialogs?: number }).activeDialogs ?? dialogs.filter((d) => d.status !== 'closed').length}
              {' / '}
              {kpiTotalDialogs}
            </strong>
            <span>{shopDisplayName ?? 'магазин не определён'}</span>
          </div>
        </article>

        {/* KPI 2: Среднее время первого ответа */}
        <article className="metric-card" data-testid="kpi-avg-response">
          <div className="metric-icon">
            <Clock3 size={18} />
          </div>
          <div>
            <p>Ср. время ответа</p>
            <strong data-testid="kpi-avg-response-value">{kpiAvgFirstResponse}</strong>
            <span>цель: до {sla.targetFirstResponseMinutes} мин</span>
          </div>
        </article>

        {/* KPI 3: Диалоги без ответа */}
        <article className="metric-card" data-testid="kpi-no-response">
          <div className="metric-icon">
            <AlertTriangle size={18} />
          </div>
          <div>
            <p>Без ответа</p>
            <strong data-testid="kpi-no-response-value">{kpiNoResponse}</strong>
            <span>требуют контроля</span>
          </div>
        </article>

        {/* KPI 4: Загрузка сотрудников */}
        <article className="metric-card" data-testid="kpi-operator-load">
          <div className="metric-icon">
            <UsersRound size={18} />
          </div>
          <div>
            <p>Загрузка</p>
            <strong data-testid="kpi-operator-load-value">
              {kpiTotalOperators > 0
                ? `${kpiOnShiftCount} / ${kpiTotalOperators}`
                : '—'}
            </strong>
            <span>на смене / всего</span>
          </div>
        </article>

        {/* KPI 5: Канал поступления */}
        <article className="metric-card" data-testid="kpi-top-channel">
          <div className="metric-icon">
            <Route size={18} />
          </div>
          <div>
            <p>Канал поступления</p>
            <strong data-testid="kpi-top-channel-value">
              {topChannel !== '—'
                ? topChannel.charAt(0).toUpperCase() + topChannel.slice(1)
                : '—'}
            </strong>
            <span>
              {topChannel !== '—' && channelCounts[topChannel]
                ? `${channelCounts[topChannel]} диал.`
                : 'нет данных'}
            </span>
          </div>
        </article>

        {/* KPI 6: Количество переводов */}
        <article className="metric-card" data-testid="kpi-transfers">
          <div className="metric-icon">
            <TrendingUp size={18} />
          </div>
          <div>
            <p>Переводы</p>
            <strong data-testid="kpi-transfers-value">
              {kpiTransfers > 0 ? kpiTransfers : '—'}
            </strong>
            <span>за сегодня</span>
          </div>
        </article>

        {/* KPI 7: Количество эскалаций */}
        <article className="metric-card" data-testid="kpi-escalations">
          <div className="metric-icon">
            <Route size={18} />
          </div>
          <div>
            <p>Эскалаций</p>
            <strong data-testid="kpi-escalations-value">{kpiEscalations}</strong>
            <span>в работе</span>
          </div>
        </article>

        {/* KPI 8: Отложенные диалоги */}
        <article className="metric-card" data-testid="kpi-call-later">
          <div className="metric-icon">
            <Clock3 size={18} />
          </div>
          <div>
            <p>Отложенные</p>
            <strong data-testid="kpi-call-later-value">{kpiCallLater}</strong>
            <span>нет ответа 3 мин</span>
          </div>
        </article>
      </section>

      {/* ─── SLA в рамках (отдельная строка под KPI) ─────────────────────────── */}
      <section
        className="kpi-grid"
        aria-label="SLA"
        style={{ marginTop: 0, gridTemplateColumns: 'repeat(2, 1fr)' }}
        data-testid="supervisor-sla-banner"
      >
        <article className="metric-card" data-testid="supervisor-metric-sla">
          <div className="metric-icon">
            <Gauge size={18} />
          </div>
          <div>
            <p>В рамках SLA</p>
            <strong>{slaDisplay}</strong>
            <span>цель: до {sla.targetFirstResponseMinutes} мин</span>
          </div>
        </article>
        <article className="metric-card" data-testid="supervisor-metric-breached">
          <div className="metric-icon">
            <AlertTriangle size={18} />
          </div>
          <div>
            <p>Просрочено сегодня</p>
            <strong>{sla.breachedToday}</strong>
            <span>нарушений SLA</span>
          </div>
        </article>
      </section>

      <section className="main-grid">
        {/* ─── All dialogs panel ──────────────────────────────────────────────── */}
        <section
          className="panel dialog-list-panel"
          aria-labelledby="supervisor-dialogs-title"
          data-testid="supervisor-dialogs-panel"
        >
          <div className="panel-header">
            <div>
              <p className="eyebrow">Очередь</p>
              <h2 id="supervisor-dialogs-title">Все диалоги магазина</h2>
            </div>
            <span className="badge">{dialogs.length}</span>
          </div>
          <div className="dialog-list">
            {dialogs.length === 0 ? (
              <p
                style={{ padding: '16px 24px', opacity: 0.5 }}
                data-testid="supervisor-dialogs-empty"
              >
                Нет диалогов
              </p>
            ) : (
              dialogs.map((d) => {
                // Unassigned: no operator name, not yet taken by supervisor,
                // and status is 'queued' OR 'new' (CRM-side first-arrival status).
                const isUnassigned =
                  (d.status === 'queued' || d.status === 'new') &&
                  (!d.assignedOperatorName || d.assignedOperatorName.trim() === '') &&
                  d.takenBySupervisor !== true;

                // Req 1: exact arrival time + work/wait duration
                const arrivalLabel = formatArrivalTime(d.createdAt);
                const durationLabel = isUnassigned
                  ? formatWaitDuration(d.createdAt)
                  : d.status === 'assigned' || d.status === 'escalated'
                  ? formatWorkDuration(d.assignedAt, null, d.status)
                  : d.status === 'closed'
                  ? formatWorkDuration(d.assignedAt, d.updatedAt, d.status)
                  : '';

                return (
                  <div
                    key={d.id}
                    className="dialog-card"
                    data-testid={`supervisor-dialog-${d.id}`}
                  >
                    <div className="dialog-card-top">
                      <strong>{d.clientName}</strong>
                      <span className={`channel-badge ${d.channel}`}>{d.channel}</span>
                    </div>
                    <p>{d.statusLabel}</p>
                    <div className="dialog-meta">
                      <span>{d.shopName}</span>
                      {/* Req 1: exact time instead of relative label */}
                      <span style={{ fontSize: '0.78rem' }}>{arrivalLabel}</span>
                    </div>
                    {durationLabel && (
                      <div
                        className="dialog-meta"
                        style={{ marginTop: '2px', fontSize: '0.78rem', opacity: 0.8 }}
                      >
                        <span>{durationLabel}</span>
                      </div>
                    )}
                    {/* Assigned operator name */}
                    <div className="dialog-meta" style={{ marginTop: '2px' }}>
                      <span style={{ fontSize: '0.78rem', opacity: 0.75 }}>
                        {(() => {
                          const opName = d.assignedOperatorName?.trim();
                          const isSeedName = opName ? SEED_SUPERVISOR_NAMES.has(opName) : false;
                          if (opName && !isSeedName) {
                            return `Оператор: ${opName}`;
                          }
                          return d.status === 'closed' ? 'Не назначен' : 'Ожидает назначения';
                        })()}
                      </span>
                    </div>
                    {/* Supervisor can take unassigned dialog */}
                    {isUnassigned && (authName || authUserId) && (
                      <button
                        className="btn-take-dialog"
                        style={{
                          marginTop: '6px',
                          padding: '4px 10px',
                          fontSize: '0.78rem',
                          cursor: takingDialog === d.id ? 'wait' : 'pointer',
                          opacity: takingDialog === d.id ? 0.6 : 1,
                        }}
                        disabled={takingDialog === d.id}
                        onClick={() => handleTakeDialog(d.id)}
                        data-testid={`supervisor-take-dialog-${d.id}`}
                      >
                        {takingDialog === d.id ? 'Берём…' : 'Взять в работу'}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* ─── Unassigned waiting + Escalations panel ────────────────────────── */}
        <section
          className="panel conversation-panel"
          aria-labelledby="supervisor-waiting-title"
          data-testid="supervisor-waiting-panel"
        >
          <div className="panel-header">
            <div>
              <p className="eyebrow">Ожидают</p>
              <h2 id="supervisor-waiting-title">Не взяты в работу</h2>
            </div>
            <span className="badge">{unassignedDialogs.length}</span>
          </div>
          {unassignedDialogs.length === 0 && (
            <p style={{ padding: '0 24px 24px' }}>Нет ожидающих диалогов.</p>
          )}
          <ul className="telemetry-list" style={{ padding: '0 24px 24px' }}>
            {unassignedDialogs.map((d) => (
              <li key={d.id}>
                <strong>
                  {d.clientName} · {d.shopName}
                </strong>
                <span>
                  {d.channel} · {formatWaitDuration(d.createdAt)}
                </span>
              </li>
            ))}
          </ul>

          {/* Escalations section */}
          <div className="panel-header">
            <div>
              <p className="eyebrow">Эскалации</p>
              <h2>Передано выше</h2>
            </div>
          </div>
          {escalations.length === 0 && (
            <p style={{ padding: '0 24px 24px' }}>Нет эскалаций.</p>
          )}
          <ul className="telemetry-list" style={{ padding: '0 24px 24px' }}>
            {escalations.map((e) => (
              <li key={e.dialogId} data-testid={`supervisor-escalation-${e.dialogId}`} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <strong>
                  {e.clientName}
                  {e.shopName ? <span style={{ fontWeight: 400, opacity: 0.7 }}> · {e.shopName}</span> : null}
                </strong>
                <span style={{ fontSize: '0.85em', opacity: 0.8 }}>
                  Ожидает: {e.escalatedTo} · {e.elapsedLabel}
                </span>
                {e.escalatedBy && (
                  <span style={{ fontSize: '0.8em', opacity: 0.65 }}>
                    Передал: {e.escalatedBy}
                  </span>
                )}
                <span style={{ fontSize: '0.8em', opacity: 0.65 }}>
                  {e.reasonLabel}
                </span>
                {(!e.escalationState || e.escalationState === 'pending') && (
                  <button
                    className="btn-take-dialog"
                    style={{ marginTop: '4px', alignSelf: 'flex-start' }}
                    disabled={takingDialog === e.dialogId}
                    onClick={() => handleTakeDialog(e.dialogId)}
                    data-testid={`supervisor-take-escalated-${e.dialogId}`}
                  >
                    {takingDialog === e.dialogId ? 'Берём…' : 'Взять в работу'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>

        <aside className="side-stack">
          {/* ─── Employee stats (D: all employees with shift status) ──────────── */}
          <section
            className="panel"
            aria-labelledby="supervisor-empstats-title"
            data-testid="supervisor-empstats-panel"
            data-stats-scope={statsScope ?? 'loading'}
          >
            <div className="panel-header">
              <div>
                <p className="eyebrow">Статистика</p>
                <h2 id="supervisor-empstats-title">{statsPanelTitle}</h2>
              </div>
              <UsersRound size={20} />
            </div>

            {statsLoading && !statsResp && (
              <p style={{ padding: '0 24px 16px', opacity: 0.5 }}>Загружаем статистику…</p>
            )}

            {statsError && !statsResp && (
              <p style={{ padding: '0 24px 16px', color: 'var(--color-error, #e05)' }}>
                {statsError}
              </p>
            )}

            {statsResp && statsScope === 'shop' && (
              <div style={{ padding: '0 24px 16px' }}>
                {statsResp.rows.length === 0 ? (
                  <p style={{ opacity: 0.5 }} data-testid="supervisor-empstats-empty">
                    Нет данных по сотрудникам
                  </p>
                ) : (
                  statsResp.rows.map((row: ShopStatRow) => (
                    <div key={row.shopName} data-testid={`supervisor-stats-shop-${row.shopName}`}>
                      {statsResp.rows.length > 1 && (
                        <p className="eyebrow" style={{ marginBottom: '0.25rem', marginTop: '0.5rem' }}>
                          {row.shopName}
                        </p>
                      )}
                      <ul className="telemetry-list">
                        {row.employees.map((emp: EmployeeStat) => {
                          const shiftStatus = (emp as { shiftStatus?: string }).shiftStatus ?? 'off';
                          const shiftLbl = shiftStatus === 'available'
                            ? 'на смене'
                            : shiftStatus === 'break'
                            ? 'перерыв'
                            : 'не на смене';
                          return (
                            <li
                              key={emp.operatorName}
                              data-testid={`supervisor-empstat-${emp.operatorName}`}
                            >
                              <strong>
                                {emp.operatorName}
                                <span
                                  style={{
                                    marginLeft: '6px',
                                    fontSize: '0.72rem',
                                    color: shiftStatus === 'available'
                                      ? 'var(--color-success, #2a7)'
                                      : shiftStatus === 'break'
                                      ? 'var(--color-warning, #a70)'
                                      : 'var(--color-muted, #888)',
                                    fontWeight: 400,
                                  }}
                                >
                                  {shiftLbl}
                                </span>
                              </strong>
                              <span
                                title="открытых / всего за день"
                                data-testid={`supervisor-empstat-display-${emp.operatorName}`}
                              >
                                {emp.displayValue}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      <p
                        className="telemetry-total"
                        style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '0.25rem', fontWeight: 600 }}
                        data-testid={`supervisor-stats-shop-total-${row.shopName}`}
                      >
                        Итого по магазину: {row.displayValue}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

            {statsResp && statsScope === 'all' && (
              <>
                <ul className="telemetry-list" style={{ padding: '0 24px 8px' }}>
                  {statsResp.data.length === 0 ? (
                    <li data-testid="supervisor-empstats-empty">
                      <span style={{ opacity: 0.5 }}>Нет данных по сотрудникам</span>
                    </li>
                  ) : (
                    statsResp.data.map((s: EmployeeStat) => {
                      const shiftStatus = (s as { shiftStatus?: string }).shiftStatus ?? 'off';
                      const shiftLbl = shiftStatus === 'available'
                        ? 'на смене'
                        : shiftStatus === 'break'
                        ? 'перерыв'
                        : 'не на смене';
                      return (
                        <li key={s.operatorName} data-testid={`supervisor-empstat-${s.operatorName}`}>
                          <strong>
                            {s.operatorName}
                            <span
                              style={{
                                marginLeft: '6px',
                                fontSize: '0.72rem',
                                color: shiftStatus === 'available'
                                  ? 'var(--color-success, #2a7)'
                                  : shiftStatus === 'break'
                                  ? 'var(--color-warning, #a70)'
                                  : 'var(--color-muted, #888)',
                                fontWeight: 400,
                              }}
                            >
                              {shiftLbl}
                            </span>
                          </strong>
                          <span
                            title="открытых / всего за день"
                            data-testid={`supervisor-empstat-display-${s.operatorName}`}
                          >
                            {s.displayValue}
                          </span>
                        </li>
                      );
                    })
                  )}
                </ul>
                {statsResp.data.length > 0 && (
                  <p
                    className="telemetry-total"
                    style={{ padding: '0 24px 16px', fontSize: '0.8rem', opacity: 0.6 }}
                    data-testid="supervisor-empstats-total"
                  >
                    Итого: {statsResp.displayValue}
                  </p>
                )}
              </>
            )}

            {takeError && (
              <p style={{ padding: '4px 24px', color: 'var(--color-error, #e05)', fontSize: '0.8rem' }}>
                {takeError}
              </p>
            )}
          </section>

          {/* ─── Operator workload (E: all employees) ──────────────────────────── */}
          <section className="panel client-panel" aria-labelledby="supervisor-load-title">
            <div className="panel-header">
              <div>
                <p className="eyebrow">Нагрузка</p>
                <h2 id="supervisor-load-title">Сотрудники</h2>
              </div>
              <UsersRound size={20} />
            </div>
            {visibleLoad.length === 0 ? (
              <p
                style={{ padding: '0 24px 24px', opacity: 0.5 }}
                data-testid="supervisor-load-empty"
              >
                Нет данных по нагрузке
              </p>
            ) : (
              <ul className="telemetry-list" style={{ padding: '0 24px 24px' }}>
                {visibleLoad.map((op) => {
                  const shiftStatus = op.availability ?? 'off';
                  const shiftLbl = shiftStatus === 'available'
                    ? 'на смене'
                    : shiftStatus === 'break'
                    ? 'перерыв'
                    : 'не на смене';
                  const totalToday = (op as { totalToday?: number }).totalToday;
                  return (
                    <li key={op.operatorId} data-testid={`supervisor-load-${op.operatorId}`}>
                      <strong>
                        {op.operatorName}
                        <span
                          style={{
                            marginLeft: '6px',
                            fontSize: '0.72rem',
                            color: shiftStatus === 'available'
                              ? 'var(--color-success, #2a7)'
                              : shiftStatus === 'break'
                              ? 'var(--color-warning, #a70)'
                              : 'var(--color-muted, #888)',
                            fontWeight: 400,
                          }}
                        >
                          {shiftLbl}
                        </span>
                      </strong>
                      <span>
                        {op.activeDialogs} акт
                        {totalToday !== undefined ? ` / ${totalToday} всего` : ''}
                        {' · ср.ответ '}
                        {op.avgResponseTime}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* ─── SLA metrics panel ─────────────────────────────────────────────── */}
          <section className="panel routing-panel" aria-labelledby="supervisor-sla-title">
            <div className="panel-header">
              <div>
                <p className="eyebrow">SLA</p>
                <h2 id="supervisor-sla-title">Метрики</h2>
              </div>
              <Gauge size={20} />
            </div>
            <dl className="details-list">
              <div>
                <dt>Цель первого ответа</dt>
                <dd>{sla.targetFirstResponseMinutes} мин</dd>
              </div>
              <div>
                <dt>Среднее время ответа</dt>
                <dd>{sla.avgFirstResponseTime || '—'}</dd>
              </div>
              <div>
                <dt>В рамках SLA</dt>
                <dd>{slaWithinDisplay}</dd>
              </div>
              <div>
                <dt>Просрочено сегодня</dt>
                <dd>{sla.breachedToday}</dd>
              </div>
            </dl>
          </section>
        </aside>
      </section>
    </>
  );
}
